import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { startPayfastCheckout } from "@/lib/payments";
import { SectionHeader, Button, Loading } from "@/components/brand";
import { formatR } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function Plans() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => api("/plans") });

  const subscribe = useMutation({
    mutationFn: async (plan) => {
      const s = await api("/subscriptions", { method: "POST", body: { plan, payment_method: "payfast" } });
      if (s.status === "pending_payment") { await startPayfastCheckout(s.id, `/plans?pf=1`); return null; }
      return s;
    },
    onSuccess: (s) => { qc.invalidateQueries({ queryKey: ["plans"] }); if (s) toast.success("Fit Pass active!"); },
    onError: (e) => toast.error(e.message),
  });
  const cancel = useMutation({ mutationFn: () => api("/subscriptions/cancel", { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["plans"] }); toast.message("Fit Pass cancelled."); }, onError: (e) => toast.error(e.message) });

  if (plans.isLoading) return <Loading />;
  const { plans: list = [], subscription: sub } = plans.data || {};

  return (
    <div data-testid="plans-screen" className="space-y-6">
      <SectionHeader title="Fit Pass" subtitle="Save on live group classes every month." />
      {sub ? (
        <div className="p-5 rounded-xl2 bg-ink text-white flex items-center gap-4" data-testid="active-subscription">
          <Zap size={28} className="text-brand" />
          <div className="flex-1">
            <div className="font-display text-lg">{sub.title} — active</div>
            <div className="font-body text-sm text-white/70">{sub.credits_left === null ? "Unlimited classes" : `${sub.credits_left} credits left`} · renews in {sub.days_left} days</div>
          </div>
          <Button testID="cancel-subscription" title="Cancel" variant="outline" small loading={cancel.isPending} onClick={() => cancel.mutate()} />
        </div>
      ) : null}
      <div className="grid md:grid-cols-3 gap-4">
        {list.map((p) => (
          <div key={p.key} data-testid={`plan-${p.key}`} className={cn("rounded-xl2 border p-5 flex flex-col", p.key === "active" ? "border-brand ring-2 ring-brand/20" : "border-divider")}>
            <div className="font-display text-2xl text-ink">{p.name}</div>
            <div className="font-body text-sm text-mutedgrey">{p.tagline}</div>
            <div className="font-display text-4xl text-brand mt-3">{formatR(p.price)}<span className="font-body text-sm text-mutedgrey">/mo</span></div>
            <ul className="mt-4 space-y-2 flex-1">
              {p.perks.map((perk) => <li key={perk} className="flex items-start gap-2 font-body text-sm text-ink"><Check size={16} className="text-brand mt-0.5 shrink-0" />{perk}</li>)}
            </ul>
            <Button testID={`subscribe-${p.key}`} title={sub ? "Change plan" : "Choose plan"} disabled={!!sub} loading={subscribe.isPending} onClick={() => subscribe.mutate(p.key)} full className="mt-4" />
          </div>
        ))}
      </div>
    </div>
  );
}
